import type { TFunction } from 'i18next';

import type { MetricSeriesSpec } from '@/components/charts/generic';
import type {
    FeedingDataSources,
    FeedingSeriesPoint,
    FeedingSourceState,
} from '@/domain/feedingDashboard';

import { FEEDING_COLORS } from './feedingChartColors';
import type { SystemLabels } from './systemLabel';

// A source with no mapping produces an all-null series. Plotting it would render an empty
// legend entry that reads as "measured nothing", so the dataset is dropped and the reason
// stated in the caption instead.
const isWired = (state: FeedingSourceState): boolean => state !== 'notConfigured';

/**
 * The feed bars and weight curves of the Feed & Weight chart (IPS statements 16 to 19),
 * without the environmental overlays.
 *
 * Weight bioscope is up to two series. Pens whose Bioscope data is ready draw a line;
 * pens still collecting show their daily estimates as points under their own legend
 * entry, because joining sparse startup estimates would imply a precision they do not
 * have. A mixed selection gets both, so one collecting pen no longer turns the ready
 * pens into points.
 */
export function buildFeedWeightCoreSeries(
    series: FeedingSeriesPoint[],
    dataSources: FeedingDataSources,
    system: SystemLabels,
    t: TFunction
): MetricSeriesSpec[] {
    const points = (selector: (point: FeedingSeriesPoint) => number | null) =>
        series.map((point) => ({ day: point.bucket, value: selector(point) }));
    const chartSeries: MetricSeriesSpec[] = [];

    if (isWired(dataSources.feedPlan)) {
        chartSeries.push({
            key: 'feedPlan',
            label: t('feedingModule.charts.feedPlan'),
            color: FEEDING_COLORS.pmsMuted,
            type: 'bar',
            // Bars behind the weight curves: a feed bar is wide enough to hide a line
            // crossing it, and the weights are what the eye should follow.
            order: 2,
            data: points((point) =>
                point.feedPlannedKg != null ? point.feedPlannedKg / 1000 : null
            ),
        });
    }
    if (isWired(dataSources.feedActual)) {
        chartSeries.push({
            key: 'feedActual',
            label: t('feedingModule.charts.feedActual'),
            color: FEEDING_COLORS.bioscopeMuted,
            type: 'bar',
            order: 2,
            data: points((point) =>
                point.feedActualKg != null ? point.feedActualKg / 1000 : null
            ),
        });
    }
    if (isWired(dataSources.weightPmsPlan)) {
        chartSeries.push({
            key: 'weightPmsPlan',
            label: t('feedingModule.charts.weightSystemPlan', { system: system.name }),
            color: FEEDING_COLORS.plan,
            type: 'line',
            axis: 'right',
            dashed: true,
            data: points((point) => point.weightPmsPlanG),
        });
    }
    if (isWired(dataSources.weightPmsActual)) {
        chartSeries.push({
            key: 'weightPmsActual',
            label: t('feedingModule.charts.weightSystemActual', { system: system.name }),
            color: FEEDING_COLORS.pms,
            type: 'line',
            axis: 'right',
            data: points((point) => point.weightPmsActualG),
        });
    }
    if (isWired(dataSources.weightBioscope)) {
        const hasLine = series.some((point) => point.weightBioscopeG != null);
        const hasCollecting = series.some((point) => point.weightBioscopeCollectingG != null);
        // With only collecting pens there is no line to draw, and an empty line entry
        // beside the points would read as a second, silent source.
        if (hasLine || !hasCollecting) {
            chartSeries.push({
                key: 'weightBioscope',
                label: t('feedingModule.charts.weightBioscope'),
                color: FEEDING_COLORS.bioscope,
                type: 'line',
                axis: 'right',
                data: points((point) => point.weightBioscopeG),
            });
        }
        if (hasCollecting) {
            chartSeries.push({
                key: 'weightBioscopeCollecting',
                label: t('feedingModule.charts.weightBioscopeCollecting'),
                color: FEEDING_COLORS.bioscope,
                type: 'line',
                axis: 'right',
                showLine: false,
                tooltipKey: 'feedingModule.charts.weightBioscopeCollectingHint',
                data: points((point) => point.weightBioscopeCollectingG),
            });
        }
    }
    return chartSeries;
}

/**
 * Labels of the series with no source at all, named under the chart so a gap is explained.
 * Each is named as its legend entry would be, so the caption carries the configured
 * system name too (IPS 40).
 */
export function unwiredSeriesLabels(
    dataSources: FeedingDataSources,
    system: SystemLabels,
    t: TFunction
): string[] {
    const entries: [keyof FeedingDataSources, string][] = [
        ['feedPlan', t('feedingModule.charts.feedPlan')],
        ['feedActual', t('feedingModule.charts.feedActual')],
        ['weightPmsPlan', t('feedingModule.charts.weightSystemPlan', { system: system.name })],
        ['weightPmsActual', t('feedingModule.charts.weightSystemActual', { system: system.name })],
    ];
    return entries
        .filter(([source]) => dataSources[source] === 'notConfigured')
        .map(([, label]) => label);
}
